import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Film, Award, Users, Globe, ArrowRight, Play, Pause, ChevronLeft, ChevronRight, ExternalLink, Share2, Facebook, Twitter, Linkedin, BookOpen, Clock, User, Calendar, Star } from 'lucide-react'
import toast from 'react-hot-toast'

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const [heroVideos, setHeroVideos] = useState([])
  const [highlights, setHighlights] = useState([])
  const [featuredPosts, setFeaturedPosts] = useState([])

  const features = [
    {
      icon: Film,
      title: 'Creative Excellence',
      description: 'Crafting compelling dramas, films, and music videos that resonate with audiences'
    },
    {
      icon: Users,
      title: 'Talented Cast',
      description: 'Working with the finest Bangladeshi actors to bring stories to life'
    },
    {
      icon: Award,
      title: 'Award Recognition',
      description: 'Nominated and awarded at national and international film festivals'
    },
    {
      icon: Globe,
      title: 'Cultural Heritage',
      description: 'Showcasing Bangladeshi culture and stories to the world'
    }
  ]

  const sampleHeroVideos = [
    {
      id: 1,
      title: 'Shikdar Films - Company Introduction',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      thumbnail: 'https://images.pexels.com/photos/7991579/pexels-photo-7991579.jpeg?auto=compress&cs=tinysrgb&w=1920',
      description: 'Discover the vision and passion behind Shikdar Films'
    },
    {
      id: 2,
      title: 'Behind the Scenes - Our Creative Process',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      thumbnail: 'https://images.pexels.com/photos/3062541/pexels-photo-3062541.jpeg?auto=compress&cs=tinysrgb&w=1920',
      description: 'Explore how we bring stories to life through filmmaking'
    },
    {
      id: 3,
      title: 'Award-Winning Productions Showcase',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      thumbnail: 'https://images.pexels.com/photos/1117132/pexels-photo-1117132.jpeg?auto=compress&cs=tinysrgb&w=1920',
      description: 'Celebrating our achievements in Bangladeshi cinema'
    }
  ]

  const portfolioHighlights = [
    {
      id: 1,
      title: 'Shadows of Dhaka',
      category: 'TV Drama',
      thumbnail: 'https://images.pexels.com/photos/7991579/pexels-photo-7991579.jpeg?auto=compress&cs=tinysrgb&w=800',
      year: 2024,
      status: 'Award Winner',
      description: 'A gripping drama exploring the lives of three families in old Dhaka, intertwined by fate and tradition.',
      festivalStatus: 'winner'
    },
    {
      id: 2,
      title: 'The River\'s Song',
      category: 'Short Film',
      thumbnail: 'https://images.pexels.com/photos/1117132/pexels-photo-1117132.jpeg?auto=compress&cs=tinysrgb&w=800',
      year: 2024,
      status: 'Official Selection',
      description: 'A poetic short film about a fisherman\'s daughter who dreams of becoming a singer.',
      festivalStatus: 'official-selection'
    },
    {
      id: 3,
      title: 'Eternal Bonds',
      category: 'Feature Film',
      thumbnail: 'https://images.pexels.com/photos/3062541/pexels-photo-3062541.jpeg?auto=compress&cs=tinysrgb&w=800',
      year: 2023,
      status: 'Best Cinematography',
      description: 'A feature film exploring the complexities of family relationships across generations.',
      festivalStatus: 'winner'
    },
    {
      id: 4,
      title: 'Voices of Heritage',
      category: 'Documentary',
      thumbnail: 'https://images.pexels.com/photos/1190297/pexels-photo-1190297.jpeg?auto=compress&cs=tinysrgb&w=800',
      year: 2024,
      status: 'Best Documentary',
      description: 'A documentary showcasing traditional Bangladeshi art forms and their modern relevance.',
      festivalStatus: 'winner'
    }
  ]

  useEffect(() => {
    const savedVideos = localStorage.getItem('shikdar_films_hero_videos')
    if (savedVideos) {
      setHeroVideos(JSON.parse(savedVideos))
    } else {
      setHeroVideos(sampleHeroVideos)
      localStorage.setItem('shikdar_films_hero_videos', JSON.stringify(sampleHeroVideos))
    }

    setHighlights(portfolioHighlights)

    // Load featured blog posts
    const savedNews = localStorage.getItem('shikdar_films_news')
    if (savedNews) {
      const allNews = JSON.parse(savedNews)
      const blogPosts = allNews.filter(item => item.type === 'blog').slice(0, 3)
      setFeaturedPosts(blogPosts)
    }
  }, [])

  useEffect(() => {
    if (heroVideos.length === 0 || !isPlaying) return
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroVideos.length)
    }, 8000)

    return () => clearInterval(interval)
  }, [heroVideos.length, isPlaying])

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroVideos.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroVideos.length) % heroVideos.length)
  }

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying)
  }

  const handleShare = (platform, item) => {
    const url = window.location.href
    const text = `Check out ${item.title} by Shikdar Films!`
    
    let shareUrl = ''
    switch(platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
        break
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`
        break
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
        break
      default:
        return
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400')
    toast.success('Opening share dialog...')
  }

  const handleImageError = (e) => {
    e.target.src = 'https://images.pexels.com/photos/7991579/pexels-photo-7991579.jpeg?auto=compress&cs=tinysrgb&w=800'
  }

  const getStatusColor = (status) => {
    const colors = {
      'winner': 'bg-green-100 text-green-800 border-green-300',
      'nominated': 'bg-blue-100 text-blue-800 border-blue-300',
      'official-selection': 'bg-purple-100 text-purple-800 border-purple-300',
      'finalist': 'bg-yellow-100 text-yellow-800 border-yellow-300'
    }
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300'
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {heroVideos.length > 0 && (
          <>
            <div 
              className="absolute inset-0 z-0 transition-opacity duration-1000"
              style={{
                backgroundImage: `url(${heroVideos[currentSlide].thumbnail})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <div className="absolute inset-0 bg-black/60"></div>
            </div>
            
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <div className="mb-6 inline-flex items-center gap-2 bg-bengal-orange/20 backdrop-blur-sm px-4 py-2 rounded-full">
                <Play className="w-5 h-5 text-white" />
                <span className="text-white text-sm font-medium">Featured Video {currentSlide + 1} of {heroVideos.length}</span>
              </div>
              
              <h1 className="hero-title text-4xl md:text-6xl text-white mb-4 animate-fade-in">
                {heroVideos[currentSlide].title}
              </h1>
              
              <p className="text-lg md:text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
                {heroVideos[currentSlide].description}
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a 
                  href={heroVideos[currentSlide].videoUrl.replace('/embed/', '/watch?v=')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-bengal-orange hover:bg-bengal-red text-white font-semibold px-8 h-14 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 focus:ring-4 focus:ring-bengal-orange/50"
                >
                  <Play className="w-5 h-5" />
                  View Full Video
                  <ExternalLink className="w-4 h-4" />
                </a>
                
                <Link 
                  to="/awards"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-semibold px-8 h-14 rounded-lg border-2 border-white/30 hover:border-white/50 transition-all duration-200"
                >
                  View Awards & Recognitions
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>

            <button 
              onClick={togglePlayPause}
              className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 w-14 h-14 bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-full flex items-center justify-center transition-all duration-200 border border-white/30"
            >
              {isPlaying ? (
                <Pause className="w-6 h-6 text-white" />
              ) : (
                <Play className="w-6 h-6 text-white" />
              )}
            </button>

            <button 
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-full flex items-center justify-center transition-all duration-200 border border-white/30"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            
            <button 
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-full flex items-center justify-center transition-all duration-200 border border-white/30"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>

            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
              {heroVideos.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide 
                      ? 'bg-bengal-orange w-8' 
                      : 'bg-white/50 hover:bg-white/70'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {/* Portfolio Highlights Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Star className="w-10 h-10 text-bengal-gold" />
              <h2 className="section-title text-4xl md:text-5xl text-gray-900">
                Featured Productions
              </h2>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A curated selection of our most acclaimed and award-winning works
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {highlights.map((work) => (
              <div 
                key={work.id}
                className="bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden group border border-gray-100 hover:border-bengal-green transform hover:-translate-y-2"
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={work.thumbnail}
                    alt={work.title}
                    onError={handleImageError}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  
                  <div className="absolute top-4 left-4">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border-2 ${getStatusColor(work.festivalStatus)}`}>
                      <Award className="w-3 h-3" />
                      {work.status}
                    </span>
                  </div>

                  <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleShare('facebook', work)
                      }}
                      className="w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all duration-200"
                    >
                      <Facebook className="w-4 h-4 text-blue-600" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleShare('twitter', work)
                      }}
                      className="w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all duration-200"
                    >
                      <Twitter className="w-4 h-4 text-blue-400" />
                    </button>
                  </div>

                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white font-bold text-lg mb-1">{work.title}</h3>
                    <div className="flex items-center gap-2 text-white/80 text-sm">
                      <span>{work.category}</span>
                      <span>•</span>
                      <span>{work.year}</span>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                    {work.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Blog Posts Section */}
      {featuredPosts.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <BookOpen className="w-10 h-10 text-bengal-green" />
                <h2 className="section-title text-4xl md:text-5xl text-gray-900">
                  From Our Blog
                </h2>
              </div>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Insights into filmmaking, production updates, and behind-the-scenes stories
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              {featuredPosts.map((post) => (
                <article 
                  key={post.id}
                  className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group border border-gray-100"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={post.image}
                      alt={post.title}
                      onError={handleImageError}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-bengal-green text-white rounded-full text-xs font-semibold">
                        <BookOpen className="w-3 h-3" />
                        Blog Post
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium capitalize">
                        {post.category.replace('-', ' ')}
                      </span>
                      {post.readTime && (
                        <span className="flex items-center gap-1 text-gray-500 text-xs">
                          <Clock className="w-3 h-3" />
                          {post.readTime}
                        </span>
                      )}
                    </div>

                    <h3 className="text-xl font-semibold text-gray-900 mb-3 section-title line-clamp-2">
                      {post.title}
                    </h3>

                    <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>

                    <Link 
                      to="/news"
                      className="inline-flex items-center gap-2 text-bengal-green hover:text-bengal-orange font-medium text-sm transition-colors duration-200"
                    >
                      Read Article
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>

            <div className="text-center">
              <Link 
                to="/news"
                className="inline-flex items-center justify-center gap-2 bg-bengal-green hover:bg-bengal-orange text-white font-semibold px-8 h-12 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
              >
                View All Blog Posts
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title text-4xl md:text-5xl text-gray-900 mb-4">
              Why Choose Shikdar Films
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We bring together creativity, culture, and cinematic excellence to create unforgettable visual experiences
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <div 
                  key={index}
                  className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-bengal-green group"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-bengal-green to-bengal-orange rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 section-title">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-bengal-green to-bengal-orange">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="hero-title text-4xl md:text-5xl text-white mb-6">
            Ready to Explore Our Award-Winning Work?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Discover our dramas, films, and documentaries that have been recognized at national and international festivals.
          </p>
          <Link 
            to="/awards"
            className="inline-flex items-center justify-center gap-2 bg-white text-bengal-green hover:bg-gray-100 font-semibold px-8 h-14 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 focus:ring-4 focus:ring-white/50"
          >
            View Awards & Recognitions
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  )
}

export default Home